import { print } from 'graphql'
import gql from 'graphql-tag'
import moment from 'moment'
import { FetchType, getResponseParser } from 'ra-data-hasura'
import { CREATE, GET_ONE, UPDATE } from 'react-admin'
import { uploadBlob } from '~/helpers'

export const castTypes = {
  date: (value) => {
    if (typeof value === 'string') {
      return moment(value).toDate()
    }
    return value
  },
  Int: (value) => parseInt(value),
  String: (value) => (value?.toString !== undefined ? value.toString() : ''),
}

export const castField = (field, value) => {
  // is relation?
  if (field.args.length) return value

  const required = field.type.kind === 'NON_NULL'
  const type = (field.type.ofType || field.type).name
  // Empty is NULL where the column allows it. (String() of null is '', which
  // a CHECK such as boats_panorama_url_is_https rejects.)
  if (!required && (value === '' || value == null)) {
    return null
  }
  return (castTypes[type] || ((_) => _))(value)
}

export const castFields = (fields, data) =>
  Object.keys(data).reduce(
    (o, key) => ({
      ...o,
      [key]: fields.filter((f) => f.name === key).map((f) => castField(f, data[key]))[0],
    }),
    {}
  )

export const getFirstOfType = (field, kind = 'OBJECT') => {
  return !field ? null : field.kind === kind ? field : getFirstOfType(field.ofType || field.type)
}

export const getPKeyConstraint = (introspection, field) => {
  const constraint = introspection.types.find((t) => t.name === `${field.name}_constraint`)
  if (!constraint) {
    console.debug(
      `🏥 Could not find constraint for ${field.name} 💘 beeing optimistic and bet on '${field}_pkey'`
    )
    return `${field.name}_pkey`
  }
  const pkey = constraint.enumValues.find((ev) => ev.name.endsWith('_pkey'))
  if (!pkey) {
    console.debug(
      `🩺 Found constraint but no pkey match. Possible values are ${JSON.stringify(
        constraint.enumValues
      )}.`
    )
    return `${field.name}_pkey`
  }
  return pkey.name
}

export const getIntrospectionType = (introspectionResults, name) => {
  return introspectionResults.types.find((t) => t.name === name)
}

// todo: maybe we can get rid of skip and required if we look into introspectionResults
export interface UpsertQueryOptions {
  /** Nested relation fields to upsert alongside the record. */
  relations?: string[]
  /** Fields to leave out of the mutation entirely. */
  ignore?: string[]
  /** Fields holding files to upload before the mutation runs. */
  attachments?: string[]
}

export const upsertQuery = (
  queries,
  {
    relations: upsertRelations = [],
    ignore: upsertIgnore = [],
    attachments: upsertAttachments = [],
  }: UpsertQueryOptions = {}
) => {
  queries[CREATE] = queries[UPDATE] = {
    // Both are the same insert … on_conflict, answering `data.returning[0]` —
    // the shape ra-data-hasura parses for a CREATE. For an UPDATE it expects
    // `update_<table>_by_pk` (since 0.2), found nothing, and every edit
    // showed "Cannot read properties of undefined (reading 'hasOwnProperty')"
    // after "Element wurde aktualisiert".
    parseResponse: (introspectionResults) => (_fetchType, resource) =>
      getResponseParser(introspectionResults)(FetchType.CREATE, resource),
    query: (introspectionResults) => (resource, aorFetchType, queryType, variables) => {
      const deleteRelations = Object.keys(variables).filter((key) => key.startsWith('delete_'))
      return gql`
        mutation upsert_${resource.type.name}(
          $objects: [${resource.type.name}_insert_input!]!
          $on_conflict: ${resource.type.name}_on_conflict!
          ${deleteRelations.map((r) => `$${r}: [Int!]!`)}
        ) {
          data: insert_${resource.type.name}(objects: $objects, on_conflict: $on_conflict) {
            returning ${
              queries[GET_ONE] && queries[GET_ONE].buildFields
                ? print(queries[GET_ONE].buildFields)
                : '{}'
            }
          }
          ${deleteRelations.map((r) => `${r}(where: { id: { _in: $${r} } }) { affected_rows }`)}
        }
      `
    },
    variables:
      (introspectionResults) => async (resource, aorFetchType, params, queryType, fileStorage) => {
        const recursiveBuild = async (args, resourceType, _data, _previous, includeAll = false) => {
          let on_conflict = {
            constraint: getPKeyConstraint(introspectionResults, resourceType),
            update_columns: [] as string[],
          }

          const buildObject = async (data, previous) =>
            Object.keys(data).reduce(async (acc, key) => {
              // relation ids from new object are null
              if (key === 'id' && !data[key]) return await acc

              if (upsertIgnore.includes(key)) return await acc

              const field = resourceType.fields.find((field) => field.name === key)
              if (!field) {
                console.debug(
                  `👷 ${resource.type.name} ${aorFetchType}: field not found in resource definition ➡️ ${key}`
                )
                return await acc
              }

              const relation = getFirstOfType(field)
              if (relation && !upsertRelations.includes(relation.name)) {
                console.debug(
                  `👮 ${resource.type.name} ${aorFetchType}: relation not whitelisted ➡️ ${relation.name} (${key})`
                )
                return await acc
              }

              // TODO: find other way to handle file uploading, avoiding need for async here
              if (relation && upsertAttachments.includes(field.name)) {
                console.debug(
                  `⬆️ ${resource.type.name} ${aorFetchType}: handling file uploads ➡️ ${relation.name} (${key})`
                )
                // No files picked leaves the field undefined (react-admin 5's
                // <FileInput> has no [] default); saving a comment without a
                // picture threw here.
                const files = data[key] ?? []
                const newAttachments = files.filter((a) => a.rawFile instanceof File)
                const oldAttachments = files.filter((a) => !(a.rawFile instanceof File))

                const uploadedAttachments = await Promise.all(
                  newAttachments.map(async (file) => {
                    // The resource being saved picks the bucket: only a boat's images are public.
                    const data = await uploadBlob(
                      file.rawFile,
                      file,
                      fileStorage,
                      resource.type.name
                    )
                    return {
                      type: field.name,
                      uri: data.key,
                      title: file.title,
                      content_type: data.ContentType,
                      md5: data.md5,
                      caption: file.caption,
                    }
                  })
                )
                data[key] = oldAttachments.concat(uploadedAttachments)
              }

              const notDirty = data[key] === (previous ?? {})[key]
              const notRequired = !!relation || field.type.kind !== 'NON_NULL'
              // An unchanged relation is left alone, and so is a field the
              // form never had a value for.
              if (
                !includeAll &&
                notDirty &&
                (!!relation || (notRequired && data[key] === undefined))
              ) {
                return await acc
              }
              // An unchanged column still goes into the row, but not into
              // update_columns, so it is not written. An edit is an insert with
              // on_conflict, and Postgres checks constraints on the proposed
              // row before it finds the conflict: left out, an untouched title
              // was NULL and every appointment edit failed
              // chk_either_type_or_title_not_null.
              const writes = includeAll || !notDirty || !notRequired

              let value = castField(field, data[key])
              if (relation) {
                const relationType = getIntrospectionType(introspectionResults, relation.name)
                const build = await recursiveBuild(
                  args,
                  relationType,
                  value,
                  (previous ?? {})[key],
                  true
                )
                value = {
                  data: build.objects,
                  on_conflict: build.on_conflict,
                }
                // if dataset is array we need to potantially delete old records
                if (Array.isArray(value.data)) {
                  // delete id's not in data set
                  const keep = value.data.map(({ id }) => id)
                  const dele = (data[`${key}Ids`] || []).filter((id) => !keep.includes(id))
                  // only add arg if there is somthing to delete
                  if (dele.length) {
                    args[`delete_${relationType.name}`] = dele
                  }
                  // if there also no data skip field
                  else if (!value.data.length) {
                    return await acc
                  }
                }
              } else if (writes) {
                on_conflict.update_columns.push(key)
              }

              return {
                ...(await acc),
                [key]: value,
              }
            }, {})

          let objects
          if (Array.isArray(_data)) {
            const hasOrder = resourceType.fields.find((field) => field.name === '_order')
            objects = []
            for (let i = 0; i < _data.length; i++) {
              if (hasOrder && _data.length > 1) {
                _data[i]['_order'] = i
              }
              objects[i] = await buildObject(_data[i], _previous ? _previous[i] : undefined)
            }
          } else {
            objects = await buildObject(_data, _previous)
          }

          return { objects, on_conflict }
        }
        const args = {}
        const { objects, on_conflict } = await recursiveBuild(
          args,
          resource.type,
          [params.data],
          [params.previousData]
        )
        return { objects, on_conflict, ...args }
      },
  }
  return queries
}

export default upsertQuery
